import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3,
  Download,
  FileText,
  TrendingUp,
  Calendar,
  Filter,
  ChevronDown,
  Table,
  PieChart as PieChartIcon,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { useReports } from '@/hooks/useReports';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { BarChart, PieChart as RePieChart } from '@/components/charts';
import { format, subDays } from 'date-fns';
import { cn, formatCurrency } from '@/lib/utils';
import {
  exportToCSV,
  exportInventoryValuationToPDF,
  exportPurchaseAnalyticsToPDF,
  exportPurchaseForecastToPDF,
} from '@/lib/exportUtils';

export default function ReportsPage() {
  const { user } = useAuth();
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const {
    getInventoryValuation,
    getConsumptionTrends,
    getStockMovementAudit,
    getPurchaseAnalytics,
    getPurchaseForecast,
  } = useReports();

  const valuationParams = {
    location_id: selectedLocation !== 'all' ? selectedLocation : undefined,
    category_id: selectedCategory !== 'all' ? selectedCategory : undefined,
  };

  const dateParams = {
    start_date: dateRange.from?.toISOString().split('T')[0],
    end_date: dateRange.to?.toISOString().split('T')[0],
  };

  const { data: valuationData, isLoading: valuationLoading, refetch: refetchValuation } = getInventoryValuation(valuationParams);
  const { data: forecastData, isLoading: forecastLoading } = getPurchaseForecast({ months: 3 });
  const { data: analyticsData, isLoading: analyticsLoading } = getPurchaseAnalytics(dateParams);
  const { data: consumptionData, isLoading: consumptionLoading } = getConsumptionTrends(dateParams);

  const reportTypes = [
    {
      id: 'valuation',
      name: 'Inventory Valuation',
      description: 'Current stock value by location, category, and item',
      icon: BarChart3,
      color: 'bg-blue-100 text-blue-600',
      hasChart: true,
    },
    {
      id: 'forecast',
      name: 'Purchase Forecast',
      description: 'Predictive ordering based on consumption trends',
      icon: TrendingUp,
      color: 'bg-green-100 text-green-600',
      hasChart: false,
    },
    {
      id: 'analytics',
      name: 'Purchase Analytics',
      description: 'Spending analysis by vendor and category',
      icon: PieChartIcon,
      color: 'bg-purple-100 text-purple-600',
      hasChart: true,
    },
    {
      id: 'consumption',
      name: 'Consumption Trends',
      description: 'Item usage over selected time period',
      icon: TrendingUp,
      color: 'bg-orange-100 text-orange-600',
      hasChart: true,
    },
  ];

  const getReportData = (reportId: string) => {
    switch (reportId) {
      case 'valuation':
        return valuationData;
      case 'forecast':
        return forecastData;
      case 'analytics':
        return analyticsData;
      case 'consumption':
        return consumptionData;
      default:
        return null;
    }
  };

  const isReportLoading = (reportId: string) => {
    switch (reportId) {
      case 'valuation':
        return valuationLoading;
      case 'forecast':
        return forecastLoading;
      case 'analytics':
        return analyticsLoading;
      case 'consumption':
        return consumptionLoading;
      default:
        return false;
    }
  };

  const handleExportCSV = (reportId: string) => {
    const data = getReportData(reportId);
    if (!data) return;

    let csvData: any[] = [];
    let filename = reportId;

    switch (reportId) {
      case 'valuation':
        csvData = data.items || [];
        break;
      case 'forecast':
        csvData = data.items || [];
        break;
      case 'analytics':
        csvData = data.spending_by_vendor || [];
        break;
      case 'consumption':
        csvData = data.trends || data || [];
        break;
    }

    if (csvData.length > 0) {
      exportToCSV(csvData, filename);
    }
  };

  const handleExportPDF = (reportId: string) => {
    const data = getReportData(reportId);
    if (!data) return;

    switch (reportId) {
      case 'valuation':
        exportInventoryValuationToPDF(data);
        break;
      case 'forecast':
        exportPurchaseForecastToPDF(data);
        break;
      case 'analytics':
        exportPurchaseAnalyticsToPDF(data);
        break;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Reports
          </h1>
          <p className="text-text-secondary mt-1">
            Real-time inventory and procurement analytics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
          <Button variant="secondary" onClick={() => window.location.reload()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-text-muted" />
              <span className="text-sm font-medium text-text-secondary">Filters:</span>
            </div>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                <SelectItem value="main-warehouse">Main Warehouse</SelectItem>
                <SelectItem value="pharmacy">Pharmacy Storage</SelectItem>
                <SelectItem value="surgery">Surgery Storage</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="pharma">Pharmaceuticals</SelectItem>
                <SelectItem value="surgical">Surgical Supplies</SelectItem>
                <SelectItem value="lab">Laboratory</SelectItem>
                <SelectItem value="equipment">Medical Equipment</SelectItem>
              </SelectContent>
            </Select>
            <Badge variant="outline" className="text-xs">
              {format(dateRange.from || new Date(), 'MMM d')} - {format(dateRange.to || new Date(), 'MMM d, yyyy')}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          const loading = isReportLoading(report.id);
          const data = getReportData(report.id);

          return (
            <Card key={report.id} className={cn('flex flex-col transition-all hover:shadow-md', selectedReport === report.id && 'ring-2 ring-primary')}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className={`p-2 rounded-lg ${report.color}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  {loading && (
                    <RefreshCw className="h-4 w-4 animate-spin text-text-muted" />
                  )}
                </div>
                <CardTitle className="mt-4">{report.name}</CardTitle>
                <p className="text-sm text-text-secondary mt-2">
                  {report.description}
                </p>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between mt-auto">
                <div className="text-sm text-text-muted">
                  {data && (
                    <div className="space-y-1">
                      {report.id === 'valuation' && data.total_value && (
                        <p className="text-primary font-medium">
                          Total: {formatCurrency(data.total_value)}
                        </p>
                      )}
                      {report.id === 'analytics' && data.grand_total && (
                        <p className="text-primary font-medium">
                          Spend: {formatCurrency(data.grand_total)}
                        </p>
                      )}
                      {report.id === 'forecast' && data.total_recommended_value && (
                        <p className="text-primary font-medium">
                          Recommended: {formatCurrency(data.total_recommended_value)}
                        </p>
                      )}
                      {report.id === 'consumption' && data.period && (
                        <p className="text-primary font-medium">
                          {data.period}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setSelectedReport(report.id)}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View
                  </Button>
                  {data && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleExportCSV(report.id)}
                      title="Export CSV"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Report Preview Modal */}
      <Dialog open={!!selectedReport} onOpenChange={setSelectedReport}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>
                  {selectedReport
                    ? reportTypes.find((r) => r.id === selectedReport)?.name
                    : 'Report'}
                </DialogTitle>
                <DialogDescription>
                  Generated at: {new Date().toLocaleString()}
                </DialogDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectedReport && handleExportCSV(selectedReport)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectedReport && handleExportPDF(selectedReport)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  PDF
                </Button>
              </div>
            </div>
          </DialogHeader>

          {isReportLoading(selectedReport || '') && (
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-8 w-1/2" />
              <Skeleton className="h-32 w-full" />
            </div>
          )}

          {!isReportLoading(selectedReport || '') && selectedReport && (
            <ReportPreview data={getReportData(selectedReport)} reportType={selectedReport} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReportPreview({
  data,
  reportType,
}: {
  data: any;
  reportType: string;
}) {
  if (!data) {
    return (
      <div className="text-center py-8">
        <p className="text-text-muted">No data available for this report.</p>
      </div>
    );
  }

  if (reportType === 'valuation') {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Total Inventory Value: {formatCurrency(data.total_value || 0)}
          </h3>
          <span className="text-sm text-text-muted">{(data.items || []).length} items</span>
        </div>

        {data.items && data.items.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Top 5 Items by Value</CardTitle>
              </CardHeader>
              <CardContent>
                <RePieChart
                  data={(data.items || [])
                    .sort((a: any, b: any) => b.total_value - a.total_value)
                    .slice(0, 5)
                    .map((item: any, index: number) => ({
                      name: item.item_name?.substring(0, 20) || `Item ${index + 1}`,
                      value: item.total_value || 0,
                    }))}
                  height={200}
                  formatValue={(v) => formatCurrency(v)}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Value Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart
                  data={(data.items || [])
                    .sort((a: any, b: any) => b.total_value - a.total_value)
                    .slice(0, 5)
                    .map((item: any) => ({
                      name: item.item_name?.substring(0, 15) || 'Unknown',
                      value: item.total_value || 0,
                    }))}
                  dataKey="value"
                  xAxisKey="name"
                  height={200}
                  colors={['#2563eb']}
                  formatYAxis={(v) => `$${(v / 1000).toFixed(0)}K`}
                />
              </CardContent>
            </Card>
          </div>
        )}

        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-background-subtle">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Item</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Location</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Qty</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Unit Cost</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Total Value</th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map((item: any) => (
                <tr key={item.item_id} className="border-t hover:bg-background-subtle">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium">{item.item_name}</p>
                      <p className="text-xs text-text-muted">{item.item_code}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{item.location_name}</td>
                  <td className="px-4 py-3 text-right text-sm">{item.quantity?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-sm">{formatCurrency(item.unit_cost || 0)}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium">{formatCurrency(item.total_value || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (reportType === 'forecast') {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Total Recommended Order Value: {formatCurrency(data.total_recommended_value || 0)}
          </h3>
          <span className="text-sm text-text-muted">
            Forecast period: {data.period_months} months
          </span>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-background-subtle">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Item</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Current Stock</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Monthly Need</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Recommended Qty</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-text-secondary">Urgency</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Value</th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map((item: any) => (
                <tr key={item.item_id} className="border-t hover:bg-background-subtle">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium">{item.item_name}</p>
                      <p className="text-xs text-text-muted">{item.item_code}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm">{item.current_stock}</td>
                  <td className="px-4 py-3 text-right text-sm">{item.estimated_monthly_need}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium">{item.recommended_order_qty}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge
                      variant={
                        item.urgency === 'critical'
                          ? 'error'
                          : item.urgency === 'high'
                          ? 'warning'
                          : item.urgency === 'medium'
                          ? 'default'
                          : 'info'
                      }
                    >
                      {item.urgency}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium">
                    {formatCurrency(item.recommended_order_qty * item.estimated_monthly_need)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (reportType === 'analytics') {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Total Spend: {formatCurrency(data.grand_total || 0)}
          </h3>
          <span className="text-sm text-text-muted">
            {(data.summary?.total_vendors || 0)} vendors, {(data.summary?.total_pos || 0)} POs
          </span>
        </div>

        {data.spending_by_vendor && data.spending_by_vendor.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Spending by Vendor</CardTitle>
              </CardHeader>
              <CardContent>
                <RePieChart
                  data={(data.spending_by_vendor || []).map((vendor: any, index: number) => ({
                    name: vendor.vendor_name?.substring(0, 20) || `Vendor ${index + 1}`,
                    value: vendor.total_spent || 0,
                  }))}
                  height={220}
                  formatValue={(v) => formatCurrency(v)}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Top Vendors by Spend</CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart
                  data={(data.spending_by_vendor || [])
                    .sort((a: any, b: any) => b.total_spent - a.total_spent)
                    .slice(0, 5)
                    .map((vendor: any) => ({
                      name: vendor.vendor_name?.substring(0, 15) || 'Unknown',
                      value: vendor.total_spent || 0,
                    }))}
                  dataKey="value"
                  xAxisKey="name"
                  height={220}
                  colors={['#16a34a']}
                  formatYAxis={(v) => `$${(v / 1000).toFixed(0)}K`}
                />
              </CardContent>
            </Card>
          </div>
        )}

        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-background-subtle">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Vendor</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Total Spent</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">PO Count</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {(data.spending_by_vendor || []).map((vendor: any) => (
                <tr key={vendor.vendor_id} className="border-t hover:bg-background-subtle">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium">{vendor.vendor_name}</p>
                      <p className="text-xs text-text-muted">{vendor.vendor_code}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium">
                    {formatCurrency(vendor.total_spent)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm">{vendor.po_count}</td>
                  <td className="px-4 py-3 text-right text-sm">{vendor.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (reportType === 'consumption') {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Consumption Trends
          </h3>
          <span className="text-sm text-text-muted">
            Based on selected date range
          </span>
        </div>

        {data.trends && data.trends.length > 0 && (
          <Card>
            <CardContent className="pt-4">
              <BarChart
                data={data.trends.slice(-14).map((t: any) => ({
                  date: t.date?.substring(5) || '',
                  received: t.received || 0,
                  issued: t.issued || 0,
                }))}
                dataKey={['received', 'issued']}
                xAxisKey="date"
                height={300}
                colors={['#16a34a', '#dc2626']}
                showLegend
              />
            </CardContent>
          </Card>
        )}

        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-background-subtle">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">Date</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Received</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Issued</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">Net</th>
              </tr>
            </thead>
            <tbody>
              {(data.trends || []).slice(-30).reverse().map((item: any, index: number) => (
                <tr key={index} className="border-t hover:bg-background-subtle">
                  <td className="px-4 py-3 text-sm">{item.date}</td>
                  <td className="px-4 py-3 text-right text-sm text-green-600">+{item.received || 0}</td>
                  <td className="px-4 py-3 text-right text-sm text-red-600">-{item.issued || 0}</td>
                  <td className={cn('px-4 py-3 text-right text-sm font-medium', (item.received - item.issued) >= 0 ? 'text-green-600' : 'text-red-600')}>
                    {(item.received - item.issued) >= 0 ? '+' : ''}{(item.received - item.issued) || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center py-8">
      <p className="text-text-muted">Report viewer not yet implemented for {reportType}</p>
    </div>
  );
}
